import React, { useEffect, useRef, useState } from "react";
import Button from "components/crm/ui/Button";
import CheckRow from "components/crm/ui/CheckRow";
import Chip from "components/crm/ui/Chip";
import TextField from "components/crm/ui/TextField";
import { useToast } from "components/crm/ui/Toast";
import { useCrmLocale } from "components/crm/ui/crmTime";
import CallbackWhoField, {
  EMPTY_CONTACT,
  NEW_CONTACT,
  callbackRefOf,
  initialCallbackWho,
  isCallbackWhoReady,
  refKey,
} from "components/prospection/CallbackWhoField";
import WhenPicker from "components/prospection/WhenPicker";
import { apiErrorText } from 'helper/apiErrorText';
import useCallSheetShortcuts from "hooks/useCallSheetShortcuts";
import useText from "hooks/useText";
import {
  callMoments,
  defaultRetry,
  readCallRules,
  resolveWhen,
  windowClock,
} from "pages/prospection/callTime";
import { hangUpProposal, talkDuration } from "pages/prospection/liveCall";
import {
  RESULTS,
  buildConfirmBody,
  canConfirm,
  confirmMessage,
  currentAttempt,
  ordinal,
} from "pages/prospection/qualification";
import { addCompanyContact, confirmCall } from "server/crm/prospectionApi";

const CLOCK_REFRESH_MS = 30000;

// Qualification of a call: result, note, follow-up, "Confirm".
// history: calls of the company (null while loading).
// onResultChange: told of each result chosen (the sheet's status pill reads it).
// liveCall: the sheet's Wildix call, whose hang-up proposes a result.
// phoneCallId: the Wildix call the confirmation is linked to, or null.
// callRules: those of /calls/day (no-answer threshold, call window).
const CallQualification = ({
  company,
  line,
  sessionId,
  history,
  onConfirmed,
  onResultChange,
  liveCall = null,
  phoneCallId = null,
  shortcutsEnabled = true,
  callRules = null,
}) => {
  const { text, language } = useText("prospection");
  const locale = useCrmLocale();
  const toast = useToast();
  const [now, setNow] = useState(() => new Date());
  const [result, setResult] = useState(null);
  const [note, setNote] = useState("");
  const [visit, setVisit] = useState(false);
  const { noAnswerThreshold, callWindow } = readCallRules(callRules);
  const [callbackWhen, setCallbackWhen] = useState({
    choice: "tomorrow",
    day: null,
    time: "",
  });
  const [retryWhen, setRetryWhen] = useState(() => ({
    choice: defaultRetry(callMoments(new Date(), callWindow)),
    day: null,
    time: "",
  }));
  // « Rappeler qui ? » of « À rappeler », and the contacts added through it
  // (a retry after a failed confirmation calls the one already added).
  const [callbackWho, setCallbackWho] = useState(() => initialCallbackWho(line));
  const [addedPeople, setAddedPeople] = useState([]);
  const people = [...(company?.members ?? []), ...addedPeople];
  // The switchboard's number: the line's once the api gives it, else the
  // card's own when it is the standard.
  const standardPhone = line?.standardPhone ?? (company?.phoneSource === "company" ? company.phone : "");
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  // Calls already looked at on hang-up: a call proposes once, even after
  // later updates or a result changed by hand.
  const proposedCallIds = useRef(new Set());
  // A result only proposed gives way to the proposal of another call put on
  // the sheet (the call to qualify read after another one); one chosen by
  // hand stays.
  const chosenByHand = useRef(false);
  const chooseResult = (id) => {
    chosenByHand.current = true;
    setResult(id);
  };

  useEffect(() => {
    if (onResultChange) onResultChange(result);
  }, [result, onResultChange]);

  // A result chosen before the hang-up is kept, without a proposal.
  useEffect(() => {
    const proposed = hangUpProposal(liveCall);
    if (!proposed || proposedCallIds.current.has(liveCall.id)) return;
    proposedCallIds.current.add(liveCall.id);
    if (result && chosenByHand.current) return;
    setResult(proposed.result);
    setProposal(proposed);
  }, [liveCall, result]);

  // Keeps the moment chips in step with the clock.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CLOCK_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const attempt = currentAttempt(history);
  const thresholdReached = attempt >= noAnswerThreshold;
  const moments = callMoments(now, callWindow);
  let at = null;
  if (result === "callback") {
    at = resolveWhen(callbackWhen, moments, now, callWindow);
  }
  if (result === "no_answer" && !thresholdReached) {
    at = resolveWhen(retryWhen, moments, now, callWindow);
  }
  const ready =
    canConfirm({
      result,
      note,
      at,
      history,
      attempt,
      threshold: noAnswerThreshold,
    }) &&
    (result !== "callback" || isCallbackWhoReady(callbackWho));
  const outsideWindowText = text("when.timeOutsideWindow", {
    start: windowClock(callWindow.start),
    end: windowClock(callWindow.end),
  });

  // Whom « À rappeler » calls back; a new contact is added to the company first.
  const callbackContactRef = async () => {
    if (callbackWho.choice !== NEW_CONTACT) return callbackRefOf(callbackWho.choice, people);
    const contact = Object.fromEntries(
      Object.entries(callbackWho.contact).map(([field, value]) => [field, value.trim()])
    );
    const { contact: added } = await addCompanyContact({ companyId: company.id, contact });
    setAddedPeople((current) => [...current, { ...contact, ...added, account: false }]);
    setCallbackWho({ choice: refKey({ contactId: added.id }), contact: EMPTY_CONTACT });
    return { contactId: added.id };
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const body = buildConfirmBody({
        ...(result === "callback" ? { callbackContactRef: await callbackContactRef() } : {}),
        company,
        line,
        sessionId,
        result,
        note,
        visit,
        at,
        attempt,
        threshold: noAnswerThreshold,
      });
      const response = await confirmCall(
        phoneCallId ? { ...body, phoneCallId } : body
      );
      onConfirmed({
        message: confirmMessage(response, {
          company,
          now: new Date(),
          text,
          locale,
          threshold: noAnswerThreshold,
        }),
        response,
      });
    } catch (err) {
      // The moment left the window (rules changed meanwhile): same words as the picker.
      if (err?.response?.data?.errorCode === "OUTSIDE_CALL_WINDOW") {
        toast(outsideWindowText, "error");
        return;
      }
      if (err?.response?.data?.errorCode === "INVALID_CONTACT") {
        toast("Vérifiez le nouveau contact : son nom, son poste et un numéro valide.", "error");
        return;
      }
      toast(
        text("qualification.saveError", {
          error: apiErrorText(err, { language, text, group: 'errors' }),
        }),
        "error"
      );
    } finally {
      setBusy(false);
    }
  };

  // Mounted only before the confirmation: S and Enter end with it.
  useCallSheetShortcuts(
    {
      s: () => chooseResult("callback"),
      enter: () => {
        if (ready && !busy) confirm();
      },
    },
    shortcutsEnabled,
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="font-title text-sm font-medium leading-5 text-body">
        {text("qualification.title")}
      </p>
      {proposal && (
        <p className="text-sm leading-5 text-body">
          {text(`qualification.proposal.${proposal.line}`, {
            duration: talkDuration(proposal.seconds, text),
          })}{" "}
          {text("qualification.proposal.check")}
        </p>
      )}
      <div className="flex flex-wrap gap-2" data-enter-confirms="">
        {RESULTS.map((id) => (
          <Chip key={id} selected={result === id} onClick={() => chooseResult(id)}>
            {text(`results.${id}`)}
          </Chip>
        ))}
      </div>

      {result === "answered" && (
        <CheckRow checked={visit} onClick={() => setVisit((value) => !value)}>
          {text("qualification.visit")}
        </CheckRow>
      )}

      {result === "callback" && (
        <WhenPicker
          id="callback-when"
          title={text("qualification.callbackTitle")}
          hint={text("qualification.callbackHint")}
          value={callbackWhen}
          onChange={setCallbackWhen}
          now={now}
          callWindow={callWindow}
        />
      )}

      {result === "callback" && (
        <CallbackWhoField
          people={people}
          standardPhone={standardPhone}
          value={callbackWho}
          onChange={setCallbackWho}
        />
      )}

      {result === "no_answer" && history && !thresholdReached && (
        <WhenPicker
          id="retry-when"
          title={text("qualification.retryTitle", {
            ordinal: ordinal(attempt, text),
          })}
          hint={text("qualification.retryHint", {
            n: attempt,
            max: noAnswerThreshold,
          })}
          value={retryWhen}
          onChange={setRetryWhen}
          now={now}
          callWindow={callWindow}
        />
      )}

      {result === "no_answer" && history && thresholdReached && (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold leading-5 text-ink">
            {text("qualification.thresholdTitle", {
              ordinal: ordinal(attempt, text),
            })}
          </p>
          <p className="text-sm leading-5 text-body">
            {text("qualification.thresholdVisit")}
          </p>
        </div>
      )}

      <TextField
        id="call-note"
        multiline
        label={text("qualification.noteLabel")}
        hint={text(
          result === "no_answer"
            ? "qualification.noteOptional"
            : "qualification.noteRequired"
        )}
        placeholder={text("qualification.notePlaceholder")}
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />

      <div className="flex justify-end">
        <Button onClick={confirm} disabled={!ready || busy}>
          {busy ? text("qualification.saving") : text("qualification.confirm")}
        </Button>
      </div>
    </div>
  );
};

export default CallQualification;
